import { ApolloQueryResult, QueryResult } from '@apollo/client';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Form, { Field, FieldProps, FormApi, FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import { RadioGroup } from '@atlaskit/radio';
import SectionMessage from '@atlaskit/section-message';
import { PaymentMethodId, PaymentMethodPanel } from '@atlassian/commerce-payment-methods';
import {
  CreditCard,
  FetchPaymentMethodsForTransactionAccountQuery,
  PaymentMethod,
  PaymentMethodType,
  PaymentOnTerms,
} from '@css-xp/graphtypes';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { setDefaultPaymentMethod } from '@atlassian/temp-proxies';
import get from 'lodash/get';
import set from 'lodash/set';
import React from 'react';
import { ErrorMessageOnInfoPanel, GenericErrorPage } from '../../error-page';
import { useFeatureToggle, useFormSubmit, useNotification, useSidebar } from '../../hooks';
import { InfoPanel } from '../../info-panel';
import { successNotificationBuilder } from '../../notifications/utils/notification-builder';
import { fetchPaymentMethodsForTransactionAccount } from './queries/fetch-payment-methods-for-transaction-account';
import { abgGraphqlClient as graphqlClient } from '@atlassian/temp-client-utils';
import InflightInvoiceBanner from '../../inflight-invoice-banner/inflight-invoice-banner';
interface FormData {
  paymentMethod?: PaymentMethod | null;
}

export interface PaymentMethodRequest {
  transactionAccountId: string;
  invoiceGroupId?: string;
  id: string;
}

interface Props {
  transactionAccountId: string;
  refetch?: () => Promise<ApolloQueryResult<unknown>>;
  invoiceGroupId?: string;
  isPaymentOnTermsDisabled?: boolean;
  submitAction?: (request: PaymentMethodRequest) => Promise<PaymentMethod>;
}

export const getPaymentMethodOption = (
  paymentMethod: PaymentMethod,
  index: number,
  isDisabledPaymentMethod: boolean,
  defaultPaymentMethodId: string | undefined | null
) => {
  switch (paymentMethod.type) {
    case PaymentMethodType.CARD: {
      const creditCard = paymentMethod as CreditCard;

      // convert expMonth & expYear to Numbers
      const expMonth = +(creditCard.card?.expirationMonth ?? 0);
      const expYear = +(creditCard.card?.expirationYear ?? 0);
      return [
        {
          name: creditCard.id ?? '',
          value: creditCard.id ?? '',
          label: (
            <PaymentMethodPanel
              paymentMethod={{
                id: creditCard.id as PaymentMethodId,
                type: paymentMethod.type,
                card: {
                  brand: creditCard.card?.brand ?? '',
                  expMonth,
                  expYear,
                  last4: creditCard.card?.last4Numbers ?? '',
                },
                default: defaultPaymentMethodId === creditCard.id,
              }}
            />
          ),
          isDisabled: isDisabledPaymentMethod,
          testId: `payment-method-${index}`,
        },
      ];
    }

    case PaymentMethodType.DEFERRED: {
      const paymentOnTerms = paymentMethod as PaymentOnTerms;

      let deferredPaymentlabel = 'Payment on terms';
      deferredPaymentlabel = deferredPaymentlabel.concat(
        paymentOnTerms.terms?.invoiceDueDays ? ': due in ' + paymentOnTerms.terms.invoiceDueDays + ' days' : ''
      );
      return [
        {
          name: deferredPaymentlabel,
          label: (
            <PaymentMethodPanel
              paymentMethod={{
                id: paymentOnTerms.id as PaymentMethodId,
                type: paymentOnTerms.type,
                default: defaultPaymentMethodId === paymentOnTerms.id,
              }}
            />
          ),
          value: paymentOnTerms.id ?? '',
          isDisabled: isDisabledPaymentMethod,
          testId: `deferred-payment-method-${index}`,
        },
      ];
    }

    default: {
      return [];
    }
  }
};

export const SetPaymentMethod = ({
  transactionAccountId,
  invoiceGroupId,
  isPaymentOnTermsDisabled = false,
  refetch,
  submitAction = setDefaultPaymentMethod,
}: Props) => {
  const { closeSidebar } = useSidebar();

  const [paymentMethodsForTxa, setPaymentMethodsForTxa] = React.useState<
    Partial<QueryResult<FetchPaymentMethodsForTransactionAccountQuery>>
  >({ loading: true });

  const { error: formError, isProcessing, submitForm, complete } = useFormSubmit<PaymentMethodRequest, PaymentMethod>({
    submitFormAction: submitAction,
    onComplete: () => {
      closeSidebar();
      if (refetch) {
        refetch();
      } else {
        window.location.reload();
      }
    },
  });

  const [isEnabled] = useFeatureToggle();

  React.useEffect(() => {
    (async () => {
      const transactionAccountPaymentMethods = await graphqlClient().query({
        query: fetchPaymentMethodsForTransactionAccount,
        variables: { id: transactionAccountId },
        fetchPolicy: 'no-cache',
      });

      setPaymentMethodsForTxa({ ...transactionAccountPaymentMethods });
    })();
  }, []);

  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

  if (paymentMethodsForTxa.loading) return <LoadingIndicator />;
  if (paymentMethodsForTxa.error) return <GenericErrorPage error={paymentMethodsForTxa.error} />;

  const availablePaymentMethods = paymentMethodsForTxa.data?.transactionAccount?.availablePaymentMethods?.nodes;

  if (!(availablePaymentMethods && availablePaymentMethods.length > 0)) {
    return null;
  }

  const invoiceGroups = paymentMethodsForTxa.data?.transactionAccount.availableInvoiceGroups?.nodes;

  // if on an invoice group get the defaultPaymentMethod so we can display a ✅
  const invoiceGroupDefaultPaymentMethod = invoiceGroups?.find((invoiceGroup) => invoiceGroup?.id === invoiceGroupId);

  const txaDefaultPaymentMethod = availablePaymentMethods?.filter(function (paymentMethod) {
    return paymentMethod?.default;
  });

  //@ts-ignore
  const isPaymentIssue = availablePaymentMethods.some((paymentMethod) => paymentMethod?.paymentIssue === true);

  const defaultPaymentMethod = invoiceGroupId
    ? invoiceGroupDefaultPaymentMethod?.defaultPaymentMethod
    : txaDefaultPaymentMethod && txaDefaultPaymentMethod.length > 0
    ? txaDefaultPaymentMethod[0]
    : null;

  // Show only Unique Payment on terms method available and prefer to the one which is selected as default payment method in invoice group.
  const selectedPaymentOnTerm: any = [];
  let deferredPaymentMethod: PaymentMethod[] = [];

  /** Show Payment on terms option only from invoice group Page */
  if (invoiceGroupId) {
    deferredPaymentMethod =
      availablePaymentMethods
        //@ts-ignore
        ?.filter((paymentMethod: PaymentMethod) => paymentMethod.type === PaymentMethodType.DEFERRED)
        //@ts-ignore
        .map((paymentMethod: PaymentMethod) => {
          if (
            invoiceGroupDefaultPaymentMethod?.defaultPaymentMethod?.id &&
            invoiceGroupDefaultPaymentMethod?.defaultPaymentMethod?.id === paymentMethod.id
          ) {
            selectedPaymentOnTerm.push(paymentMethod);
          }
          return paymentMethod;
        }) ?? [];
  }
  const cardPaymentMethod =
    availablePaymentMethods?.filter((paymentMethod: any) => paymentMethod.type === PaymentMethodType.CARD) ?? [];

  let paymentMethods = [...selectedPaymentOnTerm, ...cardPaymentMethod];
  if (!selectedPaymentOnTerm.length && deferredPaymentMethod.length) {
    paymentMethods = [...[deferredPaymentMethod[0]], ...paymentMethods];
  }

  const submitPaymentDetails = (billingData: FormData, formApi: FormApi<FormData>) => {
    // @atlaskit/form does not handle default form values well, creating thing like `selectedAddOns[0].selectedAddOn` instead of creating an object at that index. Lodash has our back.
    const transformedData: FormData = {};
    // setting key to any as getFieldState uses the actual keys of FormData, which does not include array indices
    formApi.getRegisteredFields().forEach((key: any) => {
      // get the value from the field state or the form data
      const value = formApi.getFieldState(key)?.value ?? get(billingData, key);
      set(transformedData, key, value);
    });

    const setDefaultPaymentMethodPayload: PaymentMethodRequest = {
      transactionAccountId,
      id: get(transformedData, 'payment-method'),
      invoiceGroupId,
    };
    submitForm(setDefaultPaymentMethodPayload);
  };

  const disabledPaymentMethod = defaultPaymentMethod?.type === PaymentMethodType.DEFERRED;

  const defaultPaymentMethodId = defaultPaymentMethod?.id ? defaultPaymentMethod?.id : null;

  let paymentMethodOption: any[] = [];
  let isDeferredPaymentAvailable = false;

  if (paymentMethods) {
    paymentMethods.forEach((paymentMethod, index) => {
      if (paymentMethod.type) {
        if (paymentMethod.type === PaymentMethodType.DEFERRED) {
          paymentMethodOption = paymentMethodOption.concat(
            getPaymentMethodOption(
              paymentMethod,
              index,
              disabledPaymentMethod || isPaymentOnTermsDisabled,
              defaultPaymentMethod?.id
            )
          );
        } else {
          paymentMethodOption = paymentMethodOption.concat(
            getPaymentMethodOption(paymentMethod, index, disabledPaymentMethod, defaultPaymentMethod?.id)
          );
        }
      }
      if (paymentMethod.type === PaymentMethodType.DEFERRED) {
        isDeferredPaymentAvailable = true;
      }
    });
  }

  if (complete && !notificationSent) {
    const notificationOptions = successNotificationBuilder()
      .withId('update-billto-party-success')
      .withMessage('Successfully updated payment method.')
      .withDismissAfter(5000)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
  }

  const paymentMethodsHaveDeferredOption = (paymentMethods: any[]): boolean => {
    return paymentMethods.some((paymentMethod) => paymentMethod.type === PaymentMethodType.DEFERRED);
  };

  const getFormTitle = () => {
    if (invoiceGroupId) {
      return 'Set payment method';
    }
    return 'Set default payment method';
  };

  return (
    <div style={{ maxWidth: '500px', padding: '25px' }}>
      <Form onSubmit={submitPaymentDetails}>
        {({ formProps, submitting }) => (
          <form {...formProps}>
            <FormHeader title={getFormTitle()}></FormHeader>
            <InflightInvoiceBanner
              transactionAccountId={transactionAccountId}
              message={'All changes will be reflected to the orders included in the inflight invoice'}
            />
            <FormSection>
              {formError && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorMessageOnInfoPanel
                      title="Unable to update the payment method due to the following reason"
                      message={formError}
                    />
                  }
                />
              )}
              {isDeferredPaymentAvailable && !isPaymentOnTermsDisabled && (
                <SectionMessage appearance="warning">
                  <p>Once payment on terms is set to default it cannot be reversed</p>
                </SectionMessage>
              )}
              {isPaymentOnTermsDisabled && !disabledPaymentMethod && (
                <SectionMessage appearance="warning">
                  <p>You're not allowed to select Payment on Terms payment method</p>
                </SectionMessage>
              )}
              {isPaymentOnTermsDisabled && paymentMethodsHaveDeferredOption(paymentMethods) && (
                <div>
                  <br />
                  <SectionMessage appearance="warning">
                    <p>
                      Billing Profiles linked to Jira or Confluence subscriptions cannot be set to Net-30 Terms. Please
                      select an alternative payment method
                    </p>
                  </SectionMessage>
                </div>
              )}
            </FormSection>
            <FormSection>
              <div style={{ maxWidth: '500px' }}>
                <Field name="payment-method" isRequired id="payment-method" defaultValue={defaultPaymentMethodId || ''}>
                  {({ fieldProps: { isDisabled, ...fieldProps } }: { fieldProps: FieldProps<string> }) => (
                    <RadioGroup {...fieldProps} options={paymentMethodOption} />
                  )}
                </Field>
              </div>
            </FormSection>
            <FormFooter align="start">
              <ButtonGroup>
                <LoadingButton
                  appearance="primary"
                  type="submit"
                  isLoading={submitting || isProcessing}
                  isDisabled={disabledPaymentMethod}
                  testId={submitting || isProcessing ? 'loading-button' : 'save-button'}
                >
                  Save
                </LoadingButton>
                <Button appearance="subtle" onClick={closeSidebar} testId="close">
                  Close
                </Button>
              </ButtonGroup>
            </FormFooter>
          </form>
        )}
      </Form>
    </div>
  );
};
